import React from 'react';
import { DataGridPro, GridCellParams, GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro';
import { Box, Tooltip } from '@mui/material';
import { CitizenLessonAttendanceModel } from '../../../../api-service/clients';
import { ColorPalette } from '../../../../components/config';
import { attendanceAreaPathName, getCitizenLessonDeviationEditPath } from '../../attendance-area-pathnames';
import { authorityAreaPathName, citizenAreaPathName, citizenSocialDetailsPathName } from '../../../citizen/authority/citizen-authority-area-pathnames';
import { RestrictedCitizenLabel } from '../../components/restricted-citizen-label';

interface ICitizensOnLessonTableProps {
  citizensOnLesson: CitizenLessonAttendanceModel[];
  restrictedCitizenCount: number;
}

interface IRestrictedCitizenRow {
  citizenId: string;
  isRestricted: true;
}

type CitizenOnLessonRow = CitizenLessonAttendanceModel | IRestrictedCitizenRow;

function isRestrictedRow(row: CitizenOnLessonRow): row is IRestrictedCitizenRow {
  return 'isRestricted' in row;
}

function DeviationCell({ citizen }: { citizen: CitizenLessonAttendanceModel }) {
  const { citizenId, citizenLessonDeviationId } = citizen;

  if (!citizenLessonDeviationId) {
    return <>Nej</>;
  }

  function handleOpenDeviation(event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation();
    window.open(`/${attendanceAreaPathName}/${getCitizenLessonDeviationEditPath(citizenLessonDeviationId!, citizenId)}`);
  }

  return (
    <Tooltip title="Vis særplan">
      <Box component="span" onClick={handleOpenDeviation} sx={{ cursor: 'pointer', color: ColorPalette.PrimaryMain, textDecoration: 'underline' }}>
        Ja
      </Box>
    </Tooltip>
  );
}

function toYesNo(value: boolean) {
  return value ? 'Ja' : 'Nej';
}

const columns: GridColDef[] = [
  {
    headerName: 'Navn',
    field: 'citizenName',
    flex: 1,
    sortable: false,
    colSpan: ({ row }: GridCellParams<string, CitizenOnLessonRow>) => (isRestrictedRow(row) ? 4 : undefined),
    renderCell: ({ row }: GridRenderCellParams<string, CitizenOnLessonRow>) => (isRestrictedRow(row) ? <RestrictedCitizenLabel dense /> : row.citizenName),
  },
  {
    headerName: 'Særplan',
    field: 'hasLessonDeviation',
    width: 100,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<boolean, CitizenOnLessonRow>) => (isRestrictedRow(row) ? null : <DeviationCell citizen={row} />),
  },
  {
    headerName: 'Fravær',
    field: 'hasAbsence',
    width: 200,
    sortable: false,
    valueGetter: ({ row }: GridValueGetterParams<boolean, CitizenOnLessonRow>) => (isRestrictedRow(row) ? '' : toYesNo(row.hasAbsence)),
  },
  {
    headerName: 'Samtale',
    field: 'hasInterview',
    width: 200,
    sortable: false,
    valueGetter: ({ row }: GridValueGetterParams<boolean, CitizenOnLessonRow>) => (isRestrictedRow(row) ? '' : toYesNo(row.hasInterview)),
  },
];

export function CitizensOnLessonTable(props: ICitizensOnLessonTableProps) {
  const { citizensOnLesson, restrictedCitizenCount } = props;

  const restrictedRows: IRestrictedCitizenRow[] = Array.from({ length: restrictedCitizenCount }, (_, index) => ({ citizenId: `restricted-${index}`, isRestricted: true }));

  function handleOpenCitizen(citizenId: string) {
    window.open(`/${citizenAreaPathName}/${authorityAreaPathName}/${citizenId}/${citizenSocialDetailsPathName}`);
  }

  return (
    <DataGridPro<CitizenOnLessonRow>
      columns={columns}
      rows={[...citizensOnLesson, ...restrictedRows]}
      autoHeight
      density="compact"
      getRowId={row => row.citizenId}
      onRowClick={({ row }) => {
        if (!isRestrictedRow(row)) {
          handleOpenCitizen(row.citizenId);
        }
      }}
      disableSelectionOnClick
      pageSize={10}
      pagination
      rowsPerPageOptions={[10]}
    />
  );
}
